import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DataRow, EmptyState, StatusChip, UserAvatar } from "../../components/mui";
import { useListPage } from "../../components/design/ListPager";
import { normalizeProject, statusKey, fmtDate, fmtRelative } from "../../design/domain";
import { hasPermission } from "../../utils/permission";
import { MdAdd } from "react-icons/md";

/* ===================== 純函式工具（放在元件外避免每次 render 重建） ===================== */

/**
 * 只比較「日曆日」的天數差。
 * 後端 due_date 是 DateTimeOffset（多半落在 00:00），若直接用毫秒相減，
 * 今天到期的項目會因為「現在時刻晚於 00:00」被算成負數而誤判為逾期，故先歸零時分秒。
 */
function dayDiff(from, to) {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
}

/** 後端 activity_log.action → 中文動詞（描述文字本身另外解析出綠色 tag） */
const ACTION_TEXT = {
  create: "建立了",
  update: "更新了",
  delete: "刪除了",
  status_change: "變更了",
  assign: "指派了",
  comment: "留言於",
  review_open: "發起審核",
  review_decision: "審核了",
  review_reassign: "改派把關者",
};

/**
 * 後端描述格式為「建立任務『XXX』」這類字串，
 * 這裡把角括號內的實體名稱拆出來當設計稿的綠色 tag，其餘當動作文案。
 */
function parseActivity(a) {
  const desc = String(_.get(a, "description", "") || "");
  const m = desc.match(/「([^」]*)」/);
  const tag = m ? m[1] : "";
  const verb = ACTION_TEXT[_.get(a, "action")] || (m ? desc.slice(0, m.index) : "");
  return { tag, text: tag ? verb : desc || verb };
}

/**
 * 待審項目的 SLA 膠囊。
 * /Approval/ToMe 已由 ApprovalService.SlaLabel() 算好文字與色碼（欄位 sla / slaColor，
 * 逾期・今日到期＝紅、剩 1~2 天＝黃、剩 3 天以上＝綠，正是設計稿的三段色），
 * 因此優先沿用後端結果——與簽核中心 pages/Approval/Index.js 同一套語彙，
 * 兩頁不會出現同一筆審核卻標不同顏色。後端沒算時才由到期日自推。
 */
function slaOf(item) {
  const text = item.sla || item.sla_text || item.slaText;
  const color = item.sla_color || item.slaColor || item.slaC;
  if (text && color) return { text: String(text), color: String(color) };

  const due =
    item.dueAt ||
    item.due_at ||
    item.sla_due ||
    item.slaDue ||
    item.due_date ||
    item.dueDate ||
    item.deadline ||
    null;
  if (!due) {
    // 後端若只給文字型 SLA 就沿用，否則退回中性的「待審」
    return { text: text ? String(text) : "待審", color: color ? String(color) : "#dc6803" };
  }
  const d = dayDiff(new Date(), due);
  if (d < 0) return { text: "逾期", color: "#d92d20" };
  if (d === 0) return { text: "今日到期", color: "#d92d20" };
  if (d <= 2) return { text: `剩 ${d} 天`, color: "#dc6803" };
  return { text: `剩 ${d} 天`, color: "#0e7c66" };
}

/** 團隊績效成員陣列 → 指定 kpi 欄位平均（無資料回 null，讓上層決定 fallback） */
function kpiAvg(members, key) {
  const nums = (members || [])
    .map((m) => Number(_.get(m, ["kpi", key])))
    .filter((n) => !Number.isNaN(n));
  if (!nums.length) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}

/** 工作量沒有百分比欄位，沿用後端績效公式 workloadScore = min(100, workload * 5) */
function workloadAvg(members) {
  const nums = (members || [])
    .map((m) => Number(_.get(m, ["kpi", "workload"])))
    .filter((n) => !Number.isNaN(n));
  if (!nums.length) return null;
  const avgN = nums.reduce((a, b) => a + b, 0) / nums.length;
  return Math.min(100, Math.round(avgN * 5));
}

/** 趨勢徽章：只有拿得到上月績效才顯示增減，否則顯示破折號避免捏造數字 */
function trendOf(cur, prev) {
  if (cur == null || prev == null) return { trend: "—", up: true };
  const d = Math.round((cur - prev) * 10) / 10;
  return { trend: `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`, up: d >= 0 };
}

/* 三張清單卡共用的列樣式（設計稿 L142 / L146 / L150 完全相同） */
const LIST_ROW_SX = { justifyContent: "space-between", gap: 1, px: 2, py: 1.25 };
const LIST_ROW_NAME_SX = { fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 };
const CARD_HEAD_SX = {
  px: 2,
  py: 1.5,
  borderBottom: 1,
  borderColor: "divider",
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  gap: 1,
};
const dotSx = (color) => ({ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 });

/**
 * 後端算好的 SLA 色碼（字面 hex）直接沿用 —— 跟簽核中心同一套顏色，
 * 只是改成 Chip：字色用原色、底色疊 12% 透明度。
 */
const hexChipSx = (color) => ({ color, bgcolor: `${color}1f`, fontWeight: 600 });

/* ===================== 元件 ===================== */

function App(props) {
  const navigate = useNavigate();

  const now = useMemo(() => new Date(), []);
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1;
  const prevDate = new Date(curYear, curMonth - 2, 1);
  const prevYear = prevDate.getFullYear();
  const prevMon = prevDate.getMonth() + 1;

  /* 端點用的是 token 的 permission claim，這裡用同一份判斷，兩邊才不會不一致 */
  const canSeePerformance = useMemo(() => hasPermission("performance"), []);

  useEffect(() => {
    props.GET_DashboardOverview(null, null);
    props.GET_DashboardMyTasks(null, null);
    props.GET_DashboardOverdueTasks(null, null);
    props.GET_DashboardActivities({ count: 6 }, null, null);
    props.GET_ApprovalToMe(null, null, null);
    // 後端 ProjectService 在 Page/PageCount <= 0 時不套分頁、回傳全部；
    // 「各專案健康度」與「整體進度」都要看全部專案，不能被分頁截掉（同 Project/List.js 慣例）
    props.GET_ProjectList({ Page: 0, PageCount: 0, OrderKey: "id", OrderDirect: "DESC" }, null, null);
    /*
     * 績效資料只在有 performance 權限時才拿。
     *
     * /Performance/Team 掛的是 [PermissionOnly("performance")]，而儀表板本身只要求 dashboard。
     * 只有儀表板權限、沒有績效權限的角色（RD 就是）原本會在這裡吃到 403 空 body，
     * 一路傳到 utils/exception.js 跳出必須點掉的 window.alert ——
     * 而登入後 Redirect 導向的第一頁就是儀表板，等於每次登入都被擋一次。
     * 沒有權限就不要問，這頁本來也只是拿它補「紮實度」與趨勢箭頭。
     */
    if (canSeePerformance) {
      props.GET_PerformanceTeam({ year: curYear, month: curMonth }, null, null);
      props.GET_PerformanceTeamPrev({ year: prevYear, month: prevMon }, null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- 前往工項詳情（後端通知連結亦是此格式） ---------- */
  const goTask = (projectId, taskId) => {
    if (projectId) navigate(`/Project/Detail/${projectId}${taskId ? `?task=${taskId}` : ""}`);
    // 沒帶專案 id 就組不出工項詳情的網址，退回專案列表讓使用者自己選專案
    else navigate("/Project/List");
  };

  /**
   * 待我審核列的導向。
   * ApprovalService.GetToMe() 目前只回 reviewId / taskId / code / name…，沒有 project_id，
   * 而工項詳情路由是 /Project/Detail/:id，缺專案 id 就組不出網址。
   * 導覽文案是「點擊可直接前往處理」，所以退回簽核中心（該頁可直接通過／要求修改），
   * 而不是丟到看不到這筆審核的工項清單。後端補上 project_id 後這裡會自動走直達分支。
   */
  const goApproval = (projectId, taskId) => {
    if (projectId) navigate(`/Project/Detail/${projectId}${taskId ? `?task=${taskId}` : ""}`);
    else navigate("/Approvals");
  };

  /* ---------- 原始資料 ----------
   * 新模組 API 尚未完成，回 undefined / 404 / 非陣列都可能發生，
   * 因此每個來源都先過 arr() 保證是陣列，頁面才不會在 .map 上炸掉。 */
  const arr = (v) => (Array.isArray(v) ? v : []);
  const { overview } = props;
  const taskStats = useMemo(() => _.get(overview, "task_stats", {}) || {}, [overview]);
  const myTasks = arr(props.myTasks);
  const overdueTasks = arr(props.overdueTasks);
  const activities = arr(props.activities);
  const approvals = arr(props.approvals);
  const teamMembers = arr(_.get(props, "teamCur.members"));
  const teamPrevMembers = arr(_.get(props, "teamPrev.members"));
  const rawProjects = arr(props.projects);

  const projects = useMemo(() => rawProjects.map(normalizeProject), [rawProjects]);

  /* ---------- ① KPI ---------- */
  const kpis = useMemo(() => {
    const total = Number(taskStats.total || 0);
    const done = Number(taskStats.done || 0);
    const overdue = Number(taskStats.overdue || 0);
    const completion = total > 0 ? Math.round((done / total) * 100) : 0;
    const onTime = total > 0 ? Math.round(((total - overdue) / total) * 100) : 0;

    // 紮實度只有績效模組算得出來（重複工項偵測），沒有資料時顯示 0
    const solidityCur = kpiAvg(teamMembers, "solidity_score");
    const solidityPrev = kpiAvg(teamPrevMembers, "solidity_score");
    // 趨勢一律取「同一指標的上月績效」比較，值本身仍以儀表板統計為準
    const compCur = kpiAvg(teamMembers, "completion_rate");
    const compPrev = kpiAvg(teamPrevMembers, "completion_rate");
    const onTimeCur = kpiAvg(teamMembers, "on_time_rate");
    const onTimePrev = kpiAvg(teamPrevMembers, "on_time_rate");

    const overallProgress = projects.length
      ? Math.round(projects.reduce((a, p) => a + Number(p.pct || 0), 0) / projects.length)
      : 0;

    return [
      {
        label: "團隊完成率",
        value: String(completion),
        unit: "%",
        bar: `${completion}%`,
        color: "primary.main",
        ...trendOf(compCur, compPrev),
      },
      {
        label: "準時率",
        value: String(onTime),
        unit: "%",
        bar: `${onTime}%`,
        color: "#2563eb",
        ...trendOf(onTimeCur, onTimePrev),
      },
      {
        label: "紮實度",
        // 這個指標只有績效模組算得出來；沒有權限時顯示破折號，寫 0% 會被當成真的很差
        value: canSeePerformance ? String(solidityCur == null ? 0 : solidityCur) : "—",
        unit: canSeePerformance ? "%" : "",
        bar: canSeePerformance ? `${solidityCur == null ? 0 : solidityCur}%` : "0%",
        color: "#7c5cff",
        ...trendOf(solidityCur, solidityPrev),
      },
      {
        label: "整體進度",
        value: String(overallProgress),
        unit: "%",
        bar: `${overallProgress}%`,
        color: "#dc6803",
        // 專案平均進度沒有月度快照可比，維持破折號
        ...trendOf(null, null),
      },
    ];
  }, [taskStats, teamMembers, teamPrevMembers, projects, canSeePerformance]);

  /* ---------- ② 待我審核 ---------- */
  const apToMe = useMemo(
    () =>
      approvals.map((a, i) => {
        const sla = slaOf(a);
        return {
          // 後端主鍵是 reviewId（不是 id）；?? 而非 _.get 預設值，null 也要退回索引才不會撞 key
          key: a.reviewId ?? a.review_id ?? a.id ?? i,
          name: a.name || a.task_title || a.title || a.issue_key || a.code || "—",
          sla: sla.text,
          slaColor: sla.color,
          projectId: a.project_id ?? a.projectId,
          taskId: a.taskId ?? a.task_id ?? a.id,
        };
      }),
    [approvals]
  );

  /* ---------- ② 即將到期（7 天內）／逾期警示 ---------- */
  const dueSoon = useMemo(
    () =>
      myTasks
        .filter((t) => {
          const k = statusKey(t.status);
          if (k === "done" || k === "closed") return false;
          const due = t.due_date || t.dueDate;
          if (!due) return false;
          const d = dayDiff(new Date(), due);
          return d >= 0 && d <= 7;
        })
        .sort((a, b) => new Date(a.due_date || a.dueDate) - new Date(b.due_date || b.dueDate))
        .map((t, i) => ({
          key: t.id ?? i,
          name: t.title || t.name || "—",
          due: fmtDate(t.due_date || t.dueDate),
          projectId: t.project_id ?? t.projectId,
          taskId: t.id,
        })),
    [myTasks]
  );

  const overdueList = useMemo(
    () =>
      overdueTasks.map((t, i) => ({
        key: t.id ?? i,
        name: t.title || t.name || "—",
        due: fmtDate(t.due_date || t.dueDate),
        projectId: t.project_id ?? t.projectId,
        taskId: t.id,
      })),
    [overdueTasks]
  );

  /* ---------- ③ 各專案健康度 RAG ---------- */
  const rag = useMemo(
    () =>
      projects.map((p) => {
        const pct = Number(p.pct || 0);
        let health = "green";
        let note = "進度符合預期";

        if (p.status === "done" || pct >= 100) {
          note = "已完成驗收";
        } else if (!p.tasks) {
          // 專案開了卻沒有工項 —— 什麼都判不了，而且本身就是該追的事
          health = "amber";
          note = "尚未建立工項";
        } else if (!p.startRaw || !p.endRaw) {
          /*
           * 專案本身已經沒有起訖日欄位了（時程由底下工項推導：最早開始 ~ 最晚截止）。
           * 這裡是空的代表工項都沒填開始日／截止日，落後與否無從判定 ——
           * 所以話要說在工項上，講「專案尚未設定時程」使用者會去專案設定裡找一個
           * 早就不存在的欄位。
           */
          health = "amber";
          note = "工項尚未設定起訖日";
        } else {
          const totalDays = dayDiff(p.startRaw, p.endRaw);
          const usedDays = dayDiff(p.startRaw, new Date());
          let expected = 0;
          if (totalDays > 0) expected = Math.min(100, Math.max(0, (usedDays / totalDays) * 100));
          else expected = usedDays >= 0 ? 100 : 0;
          const gap = Math.round(expected - pct);
          if (gap > 30) {
            health = "red";
            note = `進度落後 ${gap}%`;
          } else if (gap > 15) {
            health = "amber";
            note = `進度落後 ${gap}%`;
          }
        }

        return {
          key: p.id,
          code: p.code,
          name: p.name,
          note,
          dot: health === "green" ? "success.main" : health === "amber" ? "warning.main" : "error.main",
          kind: health === "green" ? "done" : health === "amber" ? "warn" : "attention",
          label: health === "green" ? "健康" : health === "amber" ? "注意" : "風險",
        };
      }),
    [projects]
  );

  /* ---------- ③ 快速動作 ---------- */
  const quickActions = [
    // 新增工項的表單掛在專案詳情頁（需要 projectId），儀表板沒有專案脈絡，
    // 因此導到專案列表讓使用者先選專案
    { label: "新增工項", onClick: () => navigate("/Project/List") },
    { label: "新增專案", onClick: () => navigate("/Project/Add") },
    { label: "發起審核", onClick: () => navigate("/Approvals") },
    // /Timesheet 已經不是工時填寫表了（使用者：「時數沒屁用」），
    // 那一頁現在看的是「這一週每天記了什麼、哪幾天沒記」，標籤要跟著講實話
    { label: "每日工項紀錄", onClick: () => navigate("/Timesheet") },
  ];

  /* ---------- ④ 近期活動動態 ---------- */
  const nameOfAccount = useMemo(() => {
    // 活動紀錄只帶 create_id（帳號），借團隊績效的成員名單補上顯示名稱
    const map = {};
    teamMembers.forEach((m) => {
      if (m && m.account) map[m.account] = m.name || m.account;
    });
    return map;
  }, [teamMembers]);

  const feed = useMemo(
    () =>
      activities.map((a, i) => {
        const account = a.create_id || a.createId || a.actor || "";
        const actor = a.create_name || a.actor_name || nameOfAccount[account] || account || "系統";
        const parsed = parseActivity(a);
        return {
          key: a.id ?? i,
          actor,
          text: parsed.text,
          tag: parsed.tag,
          time: fmtRelative(a.create_at || a.createAt),
        };
      }),
    [activities, nameOfAccount]
  );

  /* ---------- ④ 我的工項 2x2 ---------- */
  const myStats = useMemo(() => {
    const cnt = (k) => myTasks.filter((t) => statusKey(t.status) === k).length;
    const overdueMine = myTasks.filter((t) => {
      const k = statusKey(t.status);
      if (k === "done" || k === "closed") return false;
      const due = t.due_date || t.dueDate;
      return !!due && dayDiff(new Date(), due) < 0;
    }).length;
    return [
      { count: cnt("pending"), label: "待處理", color: "text.secondary" },
      { count: cnt("progress"), label: "進行中", color: "primary.main" },
      { count: cnt("review"), label: "審核中", color: "warning.main" },
      { count: overdueMine, label: "逾期", color: "error.main" },
    ];
  }, [myTasks]);

  /* ---------- ④ 本月團隊績效 ---------- */
  const teamMini = useMemo(() => {
    const total = Number(taskStats.total || 0);
    const done = Number(taskStats.done || 0);
    const overdue = Number(taskStats.overdue || 0);
    const fallbackComp = total > 0 ? Math.round((done / total) * 100) : 0;
    const fallbackOnTime = total > 0 ? Math.round(((total - overdue) / total) * 100) : 0;
    const comp = kpiAvg(teamMembers, "completion_rate");
    const onTime = kpiAvg(teamMembers, "on_time_rate");
    const solidity = kpiAvg(teamMembers, "solidity_score");
    const workload = workloadAvg(teamMembers);
    return [
      { label: "完成率", val: `${comp == null ? fallbackComp : comp}%` },
      { label: "準時率", val: `${onTime == null ? fallbackOnTime : onTime}%` },
      { label: "紮實度", val: `${solidity == null ? 0 : solidity}%` },
      { label: "工作量", val: `${workload == null ? 0 : workload}%` },
    ];
  }, [taskStats, teamMembers]);

  /* ===================== 版面（逐字對應 DESIGN 稿 L127-L192） ===================== */
  /* ---------- 清單分頁：儀表板是總覽，每張卡最多 5 筆，其餘翻頁看 ---------- */
  const apPage = useListPage(apToMe, 5);
  const duePage = useListPage(dueSoon, 5);
  const overduePage = useListPage(overdueList, 5);
  const ragPage = useListPage(rag, 5);
  const feedPage = useListPage(feed, 5);

  return (
    <Stack spacing={2.25}>
      {/* ① 四張 KPI 卡 */}
      <Box data-tour="dash-kpi" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 1.75 }}>
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {k.label}
                </Typography>
                <StatusChip kind={k.up ? "done" : "attention"} size="small" sx={{ fontWeight: 700 }}>
                  {k.trend}
                </StatusChip>
              </Box>
              <Typography variant="h1" component="div" sx={{ fontWeight: 900, mt: 1.25 }}>
                {k.value}
                <Typography variant="h3" component="span" color="text.secondary">
                  {k.unit}
                </Typography>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={parseFloat(k.bar) || 0}
                aria-label={k.label}
                sx={{ mt: 1.5, height: 5, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: k.color } }}
              />
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ② 三欄清單卡 */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 1.75 }}>
        {/* 待我審核 */}
        <Card>
          <Box data-tour="dash-approvals" sx={CARD_HEAD_SX}>
            <Box component="span" sx={dotSx("warning.main")} />
            {"待我審核 "}
            <Box component="span" sx={{ color: "text.disabled" }}>
              {apToMe.length}
            </Box>
          </Box>
          <List disablePadding>
            {apPage.pageItems.map((a) => (
              <ListItemButton key={a.key} divider onClick={() => goApproval(a.projectId, a.taskId)} sx={LIST_ROW_SX}>
                <Typography variant="body2" component="span" sx={LIST_ROW_NAME_SX}>
                  {a.name}
                </Typography>
                <Chip size="small" label={a.sla} sx={hexChipSx(a.slaColor)} />
              </ListItemButton>
            ))}
          </List>
          {apToMe.length === 0 && <EmptyState dense text="目前無待我審核項目" />}
          {apPage.pager}
        </Card>

        {/* 即將到期 */}
        <Card>
          <Box sx={CARD_HEAD_SX}>
            <Box component="span" sx={dotSx("primary.main")} />
            {"即將到期 "}
            <Box component="span" sx={{ color: "text.disabled" }}>
              7 天內
            </Box>
          </Box>
          <List disablePadding>
            {duePage.pageItems.map((d) => (
              <ListItemButton key={d.key} divider onClick={() => goTask(d.projectId, d.taskId)} sx={LIST_ROW_SX}>
                <Typography variant="body2" component="span" sx={LIST_ROW_NAME_SX}>
                  {d.name}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  color="text.secondary"
                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
                >
                  {d.due}
                </Typography>
              </ListItemButton>
            ))}
          </List>
          {dueSoon.length === 0 && <EmptyState dense text="近 7 天無到期工項" />}
          {duePage.pager}
        </Card>

        {/* 逾期警示 */}
        <Card>
          <Box sx={CARD_HEAD_SX}>
            <Box component="span" sx={dotSx("error.main")} />
            逾期警示
          </Box>
          <List disablePadding>
            {overduePage.pageItems.map((d) => (
              <ListItemButton key={d.key} divider onClick={() => goTask(d.projectId, d.taskId)} sx={LIST_ROW_SX}>
                <Typography variant="body2" component="span" sx={LIST_ROW_NAME_SX}>
                  {d.name}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  color="error"
                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
                >
                  {d.due}
                </Typography>
              </ListItemButton>
            ))}
          </List>
          {overdueList.length === 0 && <EmptyState dense text="目前無逾期工項" />}
          {overduePage.pager}
        </Card>
      </Box>

      {/* ③ 各專案健康度 RAG + 快速動作 */}
      <Box sx={{ display: "flex", gap: 2.25, flexWrap: "wrap" }}>
        <Card sx={{ flex: 2, minWidth: 320 }}>
          <Box sx={{ ...CARD_HEAD_SX, px: 2.5 }}>
            {"各專案健康度 "}
            <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
              RAG
            </Typography>
          </Box>
          {ragPage.pageItems.map((r) => (
            <DataRow key={r.key}>
              <Box component="span" sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: r.dot, flexShrink: 0 }} />
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: 800, color: "primary.main", bgcolor: "primary.light", px: 1, py: 0.25, borderRadius: "var(--radius-sm)" }}
              >
                {r.code}
              </Typography>
              <Typography component="span" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                {r.name}
              </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                {r.note}
              </Typography>
              <StatusChip kind={r.kind} size="small">
                {r.label}
              </StatusChip>
            </DataRow>
          ))}
          {rag.length === 0 && <EmptyState dense text="尚無專案資料" />}
          {ragPage.pager}
        </Card>

        <Card data-tour="dash-quick" sx={{ flex: 1, minWidth: 240, height: "fit-content" }}>
          <CardContent>
            <Typography variant="h3" component="div" sx={{ mb: 1.75 }}>
              快速動作
            </Typography>
            <Stack spacing={1.125}>
              {quickActions.map((q) => (
                <Button
                  key={q.label}
                  type="button"
                  variant="outlined"
                  onClick={q.onClick}
                  startIcon={<MdAdd />}
                  sx={{ justifyContent: "flex-start" }}
                >
                  {q.label}
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* ④ 近期活動動態 + 我的工項 + 本月團隊績效 */}
      <Box sx={{ display: "flex", gap: 2.25, flexWrap: "wrap" }}>
        <Card sx={{ flex: 2, minWidth: 320 }}>
          <Box sx={{ ...CARD_HEAD_SX, px: 2.5, py: 2 }}>近期活動動態</Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {feedPage.pageItems.map((a) => (
              <DataRow key={a.key} sx={{ alignItems: "flex-start", flexWrap: "nowrap" }}>
                <UserAvatar name={a.actor} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography color="text.secondary">
                    <Box component="b" sx={{ color: "text.primary" }}>
                      {a.actor}
                    </Box>{" "}
                    {a.text}{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {a.tag}
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.disabled", fontWeight: 600, mt: 0.375 }}>
                    {a.time}
                  </Typography>
                </Box>
              </DataRow>
            ))}
            {feed.length === 0 && <EmptyState dense text="目前尚無活動紀錄" />}
            {feedPage.pager}
          </Box>
        </Card>

        <Stack spacing={2.25} sx={{ flex: 1, minWidth: 260 }}>
          <Card>
            <CardContent>
              <Typography variant="h3" component="div" sx={{ mb: 1.75 }}>
                我的工項
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {myStats.map((s) => (
                  <Box key={s.label} sx={{ bgcolor: "action.hover", borderRadius: "var(--radius-sm)", p: 1.75 }}>
                    <Typography variant="h1" component="div" sx={{ fontWeight: 900, color: s.color }}>
                      {s.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.25 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/*
            本月團隊績效 —— 整張卡的數字都來自 /Performance/Team。
            沒有績效權限的人拿不到資料，硬留著只會顯示一排 0%，看起來像團隊真的很糟。
            這是績效模組的內容，沒授權就不該出現在儀表板上。
          */}
          {canSeePerformance && (
            <Card>
              <CardContent>
                <Typography variant="h3" component="div" sx={{ mb: 0.75 }}>
                  本月團隊績效
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>
                  {`${curYear} 年 ${curMonth} 月`}
                </Typography>
                {teamMini.map((t) => (
                  <Box key={t.label} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.625 }}>
                      <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t.label}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 800, color: "primary.main" }}>
                        {t.val}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, parseFloat(t.val) || 0)}
                      aria-label={t.label}
                      sx={{ height: 6, bgcolor: "action.hover" }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

const mapStateToProps = (state) => {
  // 後端 /Project/List 回的是 { data_list, total_count… }，但也可能被改成純陣列，兩種都吃
  const projList = _.get(state, "Project.List", []);
  return {
    overview: _.get(state, "Dashboard.Overview", {}),
    myTasks: _.get(state, "Dashboard.MyTasks", []),
    overdueTasks: _.get(state, "Dashboard.OverdueTasks", []),
    activities: _.get(state, "Dashboard.Activities", []),
    approvals: _.get(state, "Approval.ToMe", []),
    projects: Array.isArray(projList) ? projList : _.get(projList, "data_list", []),
    teamCur: _.get(state, "Performance.Team", {}),
    teamPrev: _.get(state, "Performance.TeamPrev", {}),
  };
};

const mapDispatchToProps = (dispatch) => ({
  GET_DashboardOverview: (callback, loading) =>
    dispatch({ type: "GET_DashboardOverview", callback, loading }),
  GET_DashboardMyTasks: (callback, loading) =>
    dispatch({ type: "GET_DashboardMyTasks", callback, loading }),
  GET_DashboardOverdueTasks: (callback, loading) =>
    dispatch({ type: "GET_DashboardOverdueTasks", callback, loading }),
  GET_DashboardActivities: (payload, callback, loading) =>
    dispatch({ type: "GET_DashboardActivities", payload, callback, loading }),
  GET_ApprovalToMe: (payload, callback, loading) =>
    dispatch({ type: "GET_ApprovalToMe", payload, callback, loading }),
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_PerformanceTeam: (payload, callback, loading) =>
    dispatch({ type: "GET_PerformanceTeam", payload, callback, loading }),
  GET_PerformanceTeamPrev: (payload, callback, loading) =>
    dispatch({ type: "GET_PerformanceTeamPrev", payload, callback, loading }),
});

App.propTypes = {
  overview: PropTypes.object,
  myTasks: PropTypes.array,
  overdueTasks: PropTypes.array,
  activities: PropTypes.array,
  approvals: PropTypes.array,
  projects: PropTypes.array,
  teamCur: PropTypes.object,
  teamPrev: PropTypes.object,
  GET_DashboardOverview: PropTypes.func,
  GET_DashboardMyTasks: PropTypes.func,
  GET_DashboardOverdueTasks: PropTypes.func,
  GET_DashboardActivities: PropTypes.func,
  GET_ApprovalToMe: PropTypes.func,
  GET_ProjectList: PropTypes.func,
  GET_PerformanceTeam: PropTypes.func,
  GET_PerformanceTeamPrev: PropTypes.func,
};

App.defaultProps = {
  overview: {},
  myTasks: [],
  overdueTasks: [],
  activities: [],
  approvals: [],
  projects: [],
  teamCur: {},
  teamPrev: {},
  GET_DashboardOverview: () => {},
  GET_DashboardMyTasks: () => {},
  GET_DashboardOverdueTasks: () => {},
  GET_DashboardActivities: () => {},
  GET_ApprovalToMe: () => {},
  GET_ProjectList: () => {},
  GET_PerformanceTeam: () => {},
  GET_PerformanceTeamPrev: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
